import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Menu from '@components/v2/ui/Menu/Menu';
import { getAnchoredPaperSpacing } from '@components/v2/ui/utils/anchoredPaperSpacing';
import { Stack } from '@mui/material';
import { MoreHorizontal } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { MassActionsBarMenuItem } from './components/MassActionsBarMenuItem/MassActionsBarMenuItem';
import { MassActionsSelectionMenu } from './components/MassActionsSelectionMenu/MassActionsSelectionMenu';
import { useMassActionsBar } from './hooks/useMassActionsBar';
import { useMassActionsBarStyles } from './massActionsBar.styles';
import { MassActionsBarConfig } from './massActionsBar.types';

function MassActionsBar<T>({
  totalSelected,
  isAllSelected,
  actions,
  massActionFilter,
  title,
  slots,
  testId,
  onClickAction,
  toggleAllSelected,
  deselectAll,
}: MassActionsBarConfig<T>) {
  const { translate } = useTranslation();
  const { classes } = useMassActionsBarStyles();
  const {
    isMenuOpen,
    anchorEl,
    setAnchorEl,
    shortcutActions,
    menuActions,
    hasMenu,
    isChecked,
    isIndeterminate,
    handleToggleAllSelected,
    handleActionClick,
    openMenu,
    closeMenu,
  } = useMassActionsBar({
    actions,
    onClickAction,
    isAllSelected,
    totalSelected,
    toggleAllSelected,
    massActionFilter,
  });

  return (
    <Stack
      test-id={testId}
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flex: 1, minWidth: 0 }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Checkbox
          size="small"
          checked={isChecked}
          indeterminate={isIndeterminate}
          className={classes.checkbox}
          onChange={handleToggleAllSelected}
          slotProps={{ input: { 'aria-label': translate(LangEnum.GLOBAL_SELECTED) } }}
        />
        {title ? (
          translate(title)
        ) : (
          <MassActionsSelectionMenu
            totalSelected={totalSelected}
            toggleAllSelected={toggleAllSelected}
            deselectAll={deselectAll}
            massActionFilter={massActionFilter}
          />
        )}
        {slots?.leading}
      </Stack>
      <Stack
        ref={setAnchorEl}
        direction="row"
        sx={{ alignItems: 'center', gap: 0.5, flexShrink: 0 }}
      >
        {slots?.trailing}
        {shortcutActions.map((action) => (
          <IconButton
            key={String(action.type)}
            icon={action.icon}
            color="primary"
            ariaLabel={action.label ? translate(action.label as LangEnum) : String(action.type)}
            className={classes.actionButton}
            onClick={() => handleActionClick(action)}
          />
        ))}
        {hasMenu && (
          <IconButton
            icon={MoreHorizontal}
            color="primary"
            ariaLabel={translate(LangEnum.GLOBAL_MORE_ACTIONS)}
            ariaExpanded={isMenuOpen}
            isActive={isMenuOpen}
            testId="mass-actions-bar-more"
            className={classes.actionButton}
            onClick={openMenu}
          />
        )}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: getAnchoredPaperSpacing({ variant: 'dropdown' }) } }}
        >
          {menuActions.map((action) => (
            <MassActionsBarMenuItem
              key={String(action.type)}
              icon={action.icon}
              label={action.label as LangEnum}
              onClick={() => handleActionClick(action)}
            />
          ))}
        </Menu>
      </Stack>
    </Stack>
  );
}

export default MassActionsBar;
